// Pure rules for the Live Fleet camera tiles. Who may watch is decided on the
// server (can_watch_vehicle_camera_v1); this module only decides what a tile
// offers and when it renews, so it can be tested without a DOM.

export type CameraStatus = 'online' | 'standby' | 'offline' | 'none'
export type CameraChannel = { camera: number; online: boolean }

export type StreamPoll = {
  session_id: string
  pending: boolean
  outcome: 'pending' | 'live' | 'failed'
  streams: { camera: number; expires_at: string; src: string }[]
  failed: { camera: number; status: string; message: string }[]
  error: string | null
  expires_at: string | null
}

/** Worth asking Cartrack for a stream. Standby is a connected unit at rest. */
export function canStream(status: CameraStatus | null | undefined): boolean {
  return status === 'online' || status === 'standby'
}

/** The channel a tile opens on: the first one Cartrack says is online, else 1. */
export function defaultChannel(channels: CameraChannel[] | null | undefined): number {
  const list = Array.isArray(channels) ? channels : []
  const online = list.filter((c) => c.online).map((c) => c.camera).sort((a, b) => a - b)
  if (online.length > 0) return online[0]
  return list.length > 0 ? Math.min(...list.map((c) => c.camera)) : 1
}

/** Channels to offer as buttons, in order. At least camera 1. */
export function channelList(channels: CameraChannel[] | null | undefined): CameraChannel[] {
  const list = Array.isArray(channels) ? [...channels] : []
  if (list.length === 0) return [{ camera: 1, online: false }]
  return list.sort((a, b) => a.camera - b.camera)
}

// Renew this long before Cartrack's link expires, so the new stream is up
// before the old one stops. Cartrack answered in about 9 seconds on the probe.
export const RENEW_LEAD_MS = 45_000
// Never renew sooner than this, whatever the clock says, so a bad expires_at
// cannot turn into a request storm on one car's thin connection.
export const MIN_RENEW_MS = 30_000

/** Milliseconds from now until a live stream should be renewed. */
export function renewInMs(expiresAt: string | null, now: number = Date.now()): number {
  const t = expiresAt ? Date.parse(expiresAt) : NaN
  if (Number.isNaN(t)) return 4 * 60_000
  return Math.max(MIN_RENEW_MS, t - now - RENEW_LEAD_MS)
}

// Pause every stream after this long without a touch on the page. A watched
// camera is about 270 MB an hour of the car's own data; a page left open over
// a weekend would otherwise spend it on an empty office.
export const IDLE_PAUSE_MS = 10 * 60_000

export const CAMERA_STATUS_LABEL: Record<CameraStatus, string> = {
  online: 'Camera online',
  standby: 'Camera on standby',
  offline: 'Camera offline',
  none: 'No camera',
}

/** Why a tile is not playing, in words a manager can act on. */
export function idleReason(status: CameraStatus, canWatch: boolean): string | null {
  if (!canWatch) return 'Only this car’s manager or an admin can watch it.'
  if (status === 'none') return 'Cartrack reports no Vision camera on this car.'
  if (status === 'offline') return 'The camera is offline. It usually comes back when the car is switched on.'
  return null
}
